import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";

const FIELDS = [["feed_url", "RSS feed URL"], ["apple_url", "Apple Podcasts URL"], ["spotify_url", "Spotify URL"], ["youtube_url", "YouTube URL"]];

function LastSync({ show, result }) {
  const r = result || { at: show.last_fetch_at, status: show.last_fetch_status, item_count: show.last_fetch_item_count, error: show.last_fetch_error };
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-sm" data-testid="podcast-last-sync">
      <p><b>Last sync:</b> {r.at ? new Date(r.at).toLocaleString("en-IN") : "never"} · <b>Status:</b> <span data-testid="podcast-sync-status">{r.status || "never"}</span> · <b>Items:</b> <span data-testid="podcast-sync-count">{r.item_count ?? 0}</span></p>
      {r.error && <p className="mt-1 text-red-600" data-testid="podcast-sync-error">{r.error}</p>}
    </div>
  );
}

export function PodcastAdmin() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-podcast"], queryFn: async () => (await api.get("/admin/podcast")).data });
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { if (data) setF(Object.fromEntries(FIELDS.map(([k]) => [k, data.show[k] || ""]))); }, [data]);
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-podcast"] });
  const save = async () => {
    try { await api.put("/admin/podcast", f); toast.success("Saved"); refresh(); } catch { toast.error("Could not save"); }
  };
  const sync = async () => {
    setBusy(true);
    try { const r = (await api.post("/admin/podcast/sync")).data; setResult(r); refresh(); } catch { toast.error("Sync failed to run"); }
    setBusy(false);
  };
  const toggle = async (ep) => {
    try { await api.post(`/admin/podcast/episodes/${ep.id}/hidden`, { hidden: !ep.hidden }); refresh(); } catch { toast.error("Failed"); }
  };
  if (!data) return null;
  return (
    <div className="max-w-4xl space-y-6" data-testid="podcast-admin">
      <div>
        <h2 className="font-heading text-xl font-bold text-ink">Podcast — Jewellery Sahi Hai</h2>
        <p className="mt-1 text-sm text-muted">The feed is the source of truth. Episodes can't be edited here — change them at your podcast host and sync.</p>
      </div>
      <div className="grid gap-4 rounded-lg border border-border bg-background p-5 sm:grid-cols-2">
        {FIELDS.map(([k, label]) => (
          <div key={k} className={k === "feed_url" ? "sm:col-span-2" : ""}>
            <Label className="text-xs">{label}</Label>
            <Input className="mt-1" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`podcast-${k}`} />
          </div>
        ))}
        <div className="flex flex-wrap gap-2 sm:col-span-2">
          <Button onClick={save} className="bg-ink text-paper" data-testid="podcast-save">Save</Button>
          <Button onClick={sync} disabled={busy} variant="outline" data-testid="podcast-sync-now"><RefreshCw className={`mr-1.5 h-4 w-4 ${busy ? "animate-spin" : ""}`} />{busy ? "Syncing…" : "Sync now"}</Button>
        </div>
      </div>
      <LastSync show={data.show} result={result} />
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm" data-testid="podcast-episodes-table">
          <thead className="bg-card text-left text-xs uppercase tracking-wider text-muted">
            <tr><th className="px-4 py-3">#</th><th className="px-4 py-3">Title</th><th className="px-4 py-3">Published</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.episodes.map((ep) => (
              <tr key={ep.id} data-testid={`podcast-admin-row-${ep.slug}`}>
                <td className="px-4 py-3">{ep.episode_number ?? "—"}</td>
                <td className="px-4 py-3 font-medium text-ink">{ep.title}</td>
                <td className="px-4 py-3 text-muted">{ep.published_at ? new Date(ep.published_at).toLocaleDateString("en-IN") : ""}</td>
                <td className="px-4 py-3">{ep.hidden ? <span className="text-red-600">Hidden{ep.hidden_by === "feed" ? " (left feed)" : ""}</span> : "Visible"}</td>
                <td className="px-4 py-3 text-right"><Button size="sm" variant="ghost" onClick={() => toggle(ep)} data-testid={`podcast-toggle-${ep.slug}`}>{ep.hidden ? "Unhide" : "Hide"}</Button></td>
              </tr>
            ))}
            {data.episodes.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted">No episodes ingested yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
